/**
 * DOC (Daily Operating Control) Report Template
 *
 * This defines the structure of the DOC report matching DealerBuilt's format.
 * Account codes are mapped to departments and expense categories.
 *
 * Note: SALES_YTD, GROSS_YTD, F&I, and TOTAL_GROSS are calculated values
 * pulled from DealerBuilt's docaccountmapper, not individual account codes.
 */

export interface AccountDefinition {
  code: string;
  description: string;
  /** Sign convention: 'credit' means negate DB value, 'debit' means use as-is */
  sign?: 'credit' | 'debit';
}

export interface ExpenseCategory {
  name: string;
  displayName: string;
  accounts: AccountDefinition[];
}

/**
 * Suffix-based pattern matching config for departments with multiple suffix variants.
 * Currently only used by BODY_SHOP (suffixes: CVC, E, EG, R).
 * Allows automatic discovery of new account codes that match known base numbers + suffixes.
 */
export interface DepartmentSuffixConfig {
  /** Valid suffixes for this department, e.g. ['CVC', 'E', 'EG', 'R'] */
  suffixes: string[];
  /** Base numbers that belong in the SALES category */
  salesBaseNumbers: string[];
  /** Base numbers that belong in the FIXED category */
  fixedBaseNumbers: string[];
  /** Base numbers that belong in the OTHERS category */
  othersBaseNumbers: string[];
  /** Base numbers that are credits (sign: 'credit') — currently none for Body Shop */
  creditBaseNumbers: string[];
}

export interface DepartmentTemplate {
  name: string;
  displayName: string;
  /** Revenue section - computed values from DOC */
  hasRevenue: boolean;
  /** Whether this department has F&I (only New/Used) */
  hasFandI: boolean;
  /** Expense categories for this department */
  expenseCategories: ExpenseCategory[];
  /** Optional suffix-based pattern matching config for auto-discovering accounts */
  suffixConfig?: DepartmentSuffixConfig;
}

/**
 * NEW VEHICLE Department
 *
 * Note: 008 and 010A are credits that reduce expenses (display as negative)
 */
const NEW_VEHICLE: DepartmentTemplate = {
  name: 'NEW_VEHICLE',
  displayName: 'New',
  hasRevenue: true,
  hasFandI: true,
  expenseCategories: [
    {
      name: 'VARIABLE',
      displayName: 'Variable Expenses',
      accounts: [
        { code: '011A', description: 'Salespeople Compensation - New' },
        { code: '011B', description: 'Salespeople Compensation - New - Nissan/Volvo' },
        { code: '013A', description: 'F&I Sales Compensation - New' },
        { code: '013B', description: 'F&I Sales Compensation - New - Nissan/Volvo' },
        { code: '013X', description: 'F&I Sales Compensation Clearing' },
        { code: '014A', description: 'Delivery Expense - New' },
        { code: '014B', description: 'Delivery Expense - New - Nissan/Volvo' },
        { code: '015A', description: 'Policy Expense - New' },
        { code: '015B', description: 'Policy Expense - New - Nissan/Volvo' },
        { code: '017A', description: 'Pre-Delivery Refund - New' },
        { code: '021A', description: 'Compensation Supervisors - New' },
        { code: '021B', description: 'Compensation Supervisors - New - Nissan/Volvo' },
        { code: '024A', description: 'Salary General Manager - New' },
      ]
    },
    {
      name: 'SEMI_FIXED',
      displayName: 'Semi-Fixed Expenses',
      accounts: [
        // Marketing Allowance Income (all 008 variations are credits that reduce expenses)
        { code: '008A', description: 'Marketing Allow Income New', sign: 'credit' },
        { code: '008AK', description: 'Marketing Allow Income New KIA', sign: 'credit' },
        { code: '008B', description: 'Marketing Allow Income New - Nissan/Volvo', sign: 'credit' },
        { code: '008FCP', description: 'FCP Ford Accrual', sign: 'credit' },
        { code: '008V', description: 'Transaction Credit VW', sign: 'credit' },
        { code: '008W', description: 'IDM VW', sign: 'credit' },
        { code: '012A', description: 'Floor Plan Interest - New' },
        { code: '012B', description: 'Floor Plan Interest - New - Nissan/Volvo' },
        { code: '016A', description: 'Demo Expense - New' },
        { code: '016B', description: 'Demo Expense - New - Nissan/Volvo' },
        // Floor Plan Assistance (all 018 variations are credits that reduce expenses)
        { code: '018A', description: 'Floor Plan Assistance - New', sign: 'credit' },
        { code: '018AK', description: 'Floor Plan Assistance New KIA', sign: 'credit' },
        { code: '018B', description: 'Floor Plan Assistance - New - Nissan/Volvo', sign: 'credit' },
        { code: '019A', description: 'Advertising - New' },
        { code: '019B', description: 'Advertising - New - Nissan/Volvo' },
        { code: '023A', description: 'Other Salary & Wages - New' },
        { code: '023B', description: 'Other Salary & Wages - New - Nissan/Volvo' },
        { code: '076A', description: 'Schools & Seminars - New' },
        { code: '076B', description: 'Schools & Seminars - New - Nissan/Volvo' },
      ]
    },
    {
      name: 'FIXED',
      displayName: 'Fixed Expenses',
      accounts: [
        { code: '022A', description: 'Compensation Clerical - New' },
        { code: '022B', description: 'Compensation Clerical - New - Nissan/Volvo' },
        { code: '025A', description: 'Payroll Taxes - New' },
        { code: '025B', description: 'Payroll Taxes - New - Nissan/Volvo' },
        { code: '026A', description: 'Employee Benefits - New' },
        { code: '026B', description: 'Employee Benefits - New - Nissan/Volvo' },
        { code: '027A', description: 'Pension - New' },
        { code: '027B', description: 'Pension - New - Nissan/Volvo' },
        { code: '028A', description: 'Workers Comp Insurance - New' },
        { code: '028B', description: 'Workers Comp Insurance - New - Nissan/Volvo' },
        { code: '051A', description: 'Company Vehicle - New' },
        { code: '051B', description: 'Company Vehicle - New - Nissan/Volvo' },
        { code: '060A', description: 'Office Supplies - New' },
        { code: '060B', description: 'Office Supplies - New - Nissan/Volvo' },
        { code: '061A', description: 'Tools & Supplies - New' },
        { code: '061B', description: 'Tools & Supplies - New - Nissan/Volvo' },
        { code: '066A', description: 'Contributions - New' },
        { code: '066B', description: 'Contributions - New - Nissan/Volvo' },
        { code: '068A', description: 'Outside Services - New' },
        { code: '068B', description: 'Outside Services - New - Nissan/Volvo' },
        { code: '070A', description: 'Travel & Entertainment - New' },
        { code: '070B', description: 'Travel & Entertainment - New - Nissan/Volvo' },
        { code: '071A', description: 'Membership, Dues, Publications - New' },
        { code: '071B', description: 'Membership, Dues, Publications - New - Nissan/Volvo' },
        { code: '072A', description: 'Legal & Audit - New' },
        { code: '072B', description: 'Legal & Audit - New - Nissan/Volvo' },
        { code: '073A', description: 'Meals & Entertainment - New' },
        { code: '073B', description: 'Meals & Entertainment - New - Nissan/Volvo' },
        { code: '074A', description: 'Telephone - New' },
        { code: '074B', description: 'Telephone - New - Nissan/Volvo' },
        { code: '075A', description: 'Postage/Freight - New' },
        { code: '075B', description: 'Postage/Freight - New - Nissan/Volvo' },
        { code: '078A', description: 'Data Processing - New' },
        { code: '078B', description: 'Data Processing - New - Nissan/Volvo' },
        { code: '079A', description: 'Miscellaneous Expense - New' },
        { code: '079B', description: 'Miscellaneous Expense - New - Nissan/Volvo' },
        { code: '080A', description: 'Rent & Lease Expense - New' },
        { code: '080B', description: 'Rent & Lease Expense - New - Nissan/Volvo' },
        { code: '082A', description: 'Repairs & Maintenance - New' },
        { code: '082B', description: 'Repairs & Maintenance - New - Nissan/Volvo' },
        { code: '084A', description: 'Real Estate Taxes - New' },
        { code: '084B', description: 'Real Estate Taxes - New - Nissan/Volvo' },
        { code: '087A', description: 'Heat, Light, Water - New' },
        { code: '087B', description: 'Heat, Light, Water - New - Nissan/Volvo' },
        { code: '088A', description: 'Insurance Other - New' },
        { code: '088B', description: 'Insurance Other - New - Nissan/Volvo' },
        { code: '089A', description: 'Taxes Other - New' },
        { code: '089B', description: 'Taxes Other - New - Nissan/Volvo' },
        { code: '091A', description: 'Depreciation Equipment - New' },
        { code: '091B', description: 'Depreciation Equipment - New - Nissan/Volvo' },
      ]
    },
    {
      name: 'OTHERS',
      displayName: 'Others',
      accounts: [
        // Credits that reduce expenses
        { code: '008O', description: 'Volvo Operational Support', sign: 'credit' },
        { code: '010A', description: 'Honda HTB', sign: 'credit' },
        // Owner compensation
        { code: '020A', description: 'Compensation Owners - New' },
        { code: '020B', description: 'Compensation Owners - New - Nissan/Volvo' },
      ]
    }
  ]
};

/**
 * USED VEHICLE Department
 */
const USED_VEHICLE: DepartmentTemplate = {
  name: 'USED_VEHICLE',
  displayName: 'Used',
  hasRevenue: true,
  hasFandI: true,
  expenseCategories: [
    {
      name: 'VARIABLE',
      displayName: 'Variable Expenses',
      accounts: [
        { code: '011C', description: 'Salespeople Compensation - Used' },
        { code: '013C', description: 'F&I Sales Compensation - Used' },
        { code: '014C', description: 'Delivery Expense - Used' },
        { code: '015C', description: 'Policy Expense - Used' },
        { code: '021C', description: 'Compensation Supervisors - Used' },
        { code: '024C', description: 'Salary General Manager - Used' },
      ]
    },
    {
      name: 'SEMI_FIXED',
      displayName: 'Semi-Fixed Expenses',
      accounts: [
        { code: '012C', description: 'Floor Plan Interest - Used' },
        { code: '016C', description: 'Demo Expense - Used' },
        { code: '019C', description: 'Advertising - Used' },
        { code: '023C', description: 'Other Salary & Wages - Used' },
        { code: '076C', description: 'Schools & Seminars - Used' },
      ]
    },
    {
      name: 'FIXED',
      displayName: 'Fixed Expenses',
      accounts: [
        { code: '022C', description: 'Compensation Clerical - Used' },
        { code: '025C', description: 'Payroll Taxes - Used' },
        { code: '026C', description: 'Employee Benefits - Used' },
        { code: '027C', description: 'Pension - Used' },
        { code: '028C', description: 'Workers Comp Insurance - Used' },
        { code: '051C', description: 'Company Vehicle - Used' },
        { code: '060C', description: 'Office Supplies - Used' },
        { code: '061C', description: 'Tools & Supplies - Used' },
        { code: '066C', description: 'Contributions - Used' },
        { code: '068C', description: 'Outside Services - Used' },
        { code: '070C', description: 'Travel & Entertainment - Used' },
        { code: '071C', description: 'Membership, Dues, Publications - Used' },
        { code: '072C', description: 'Legal & Audit - Used' },
        { code: '073C', description: 'Meals & Entertainment - Used' },
        { code: '074C', description: 'Telephone - Used' },
        { code: '075C', description: 'Postage/Freight - Used' },
        { code: '078C', description: 'Data Processing - Used' },
        { code: '079C', description: 'Miscellaneous Expense - Used' },
        { code: '080C', description: 'Rent & Lease Expense - Used' },
        { code: '082C', description: 'Repairs & Maintenance - Used' },
        { code: '084C', description: 'Real Estate Taxes - Used' },
        { code: '087C', description: 'Heat, Light, Water - Used' },
        { code: '088C', description: 'Insurance Other - Used' },
        { code: '089C', description: 'Taxes Other - Used' },
        { code: '090C', description: 'Other Interest - Used' },
        { code: '091C', description: 'Depreciation Equipment - Used' },
      ]
    },
    {
      name: 'OTHERS',
      displayName: 'Others',
      accounts: [
        { code: '020C', description: 'Compensation Owners - Used' },
      ]
    }
  ]
};

/**
 * SERVICE Department
 */
const SERVICE: DepartmentTemplate = {
  name: 'SERVICE',
  displayName: 'Service',
  hasRevenue: true,
  hasFandI: false,
  expenseCategories: [
    {
      name: 'SALES',
      displayName: 'Sales Expenses',
      accounts: [
        // FCP Ford Accruals (credits that reduce expenses)
        { code: '008FCPD', description: 'FCP Ford Accrual - Service', sign: 'credit' },
        { code: '008FCPU', description: 'FCP Ford Accrual - Used', sign: 'credit' },
        { code: '021D', description: 'Compensation Supervisors - Service' },
        { code: '024D', description: 'Salary General Manager - Service' },
        { code: '023D', description: 'Other Salary & Wages - Service' },
        { code: '023DW', description: 'Other Salary & Wages - Admin Offset', sign: 'credit' },
        { code: '029D', description: 'Absentee Wages Service' },
        { code: '052D', description: 'Service Loaner Expense - Service' },
        { code: '061D', description: 'Tools & Supplies - Service' },
        { code: '062D', description: 'Advertising - Service' },
        { code: '062DA', description: 'Advertising - Service Audi' },
        { code: '067D', description: 'Policy Expense Service' },
        { code: '067DF', description: 'Policy Expense Fisher - Service' },
        { code: '069D', description: 'Warranty Rejection - Service' },
        { code: '069DL', description: 'Warranty - Loss Diag' },
        { code: '075D', description: 'Postage/Freight - Service' },
        { code: '076D', description: 'Schools & Seminars - Service' },
        { code: '092D', description: 'Equipment Repairs - Service' },
        { code: '450', description: 'Repair Shop Material' },
      ]
    },
    {
      name: 'FIXED',
      displayName: 'Fixed Expenses',
      accounts: [
        { code: '022D', description: 'Compensation Clerical - Service' },
        { code: '025D', description: 'Payroll Taxes - Service' },
        { code: '025R', description: 'Payroll Taxes - Recon' },
        { code: '026D', description: 'Employee Benefits - Service' },
        { code: '027D', description: 'Pension - Service' },
        { code: '027R', description: 'Pension - Recon' },
        { code: '028D', description: 'Workers Comp Insurance - Service' },
        { code: '051D', description: 'Company Vehicle - Service' },
        { code: '060D', description: 'Office Supplies - Service' },
        { code: '066D', description: 'Contributions - Service' },
        { code: '068D', description: 'Outside Services - Service' },
        { code: '070D', description: 'Travel & Entertainment - Service' },
        { code: '071D', description: 'Membership, Dues, Publications - Service' },
        { code: '072D', description: 'Legal & Audit - RMD Service' },
        { code: '073D', description: 'Meals & Entertainment - Service' },
        { code: '074D', description: 'Telephone - Service' },
        { code: '078D', description: 'Data Processing - Service' },
        { code: '079D', description: 'Miscellaneous Expense - Service' },
        { code: '080D', description: 'Rent & Lease Expense - Service' },
        { code: '082D', description: 'Repairs & Maintenance - Service' },
        { code: '084D', description: 'Real Estate Taxes - Service' },
        { code: '087D', description: 'Heat, Light, Water - Service' },
        { code: '088D', description: 'Insurance Other - Service' },
        { code: '089D', description: 'Taxes Other - Service' },
        { code: '090D', description: 'Other Interest - Service' },
        { code: '091D', description: 'Depreciation Equipment - Service' },
      ]
    },
    {
      name: 'OTHERS',
      displayName: 'Others',
      accounts: [
        { code: '020D', description: 'Compensation Owners - Service' },
      ]
    }
  ]
};

/**
 * PARTS Department
 */
const PARTS: DepartmentTemplate = {
  name: 'PARTS',
  displayName: 'Parts',
  hasRevenue: true,
  hasFandI: false,
  expenseCategories: [
    {
      name: 'SALES',
      displayName: 'Sales Expenses',
      accounts: [
        { code: '021F', description: 'Compensation Supervisors - Parts' },
        { code: '024F', description: 'Salary General Manager - Parts' },
        { code: '023F', description: 'Other Salary & Wages - Parts' },
        { code: '061F', description: 'Tools & Supplies - Parts' },
        { code: '062F', description: 'Advertising - Parts' },
        { code: '067F', description: 'Policy Expense Parts' },
        { code: '067FF', description: 'Policy Expense Fisher - Parts' },
        { code: '069F', description: 'Warranty Rejection - Parts' },
        { code: '075F', description: 'Postage/Freight - Parts' },
        { code: '076F', description: 'Schools & Seminars - Parts' },
      ]
    },
    {
      name: 'FIXED',
      displayName: 'Fixed Expenses',
      accounts: [
        { code: '022F', description: 'Compensation Clerical - Parts' },
        { code: '025F', description: 'Payroll Taxes - Parts' },
        { code: '026F', description: 'Employee Benefits - Parts' },
        { code: '027F', description: 'Pension - Parts' },
        { code: '028F', description: 'Workers Comp Insurance - Parts' },
        { code: '051F', description: 'Company Vehicle - Parts' },
        { code: '060F', description: 'Office Supplies - Parts' },
        { code: '066F', description: 'Contributions - Parts' },
        { code: '068F', description: 'Outside Services - Parts' },
        { code: '070F', description: 'Travel & Entertainment - Parts' },
        { code: '071F', description: 'Membership, Dues, Publications - Parts' },
        { code: '072F', description: 'Legal & Audit - RMD Parts' },
        { code: '073F', description: 'Meals & Entertainment - Parts' },
        { code: '074F', description: 'Telephone - Parts' },
        { code: '078F', description: 'Data Processing - Parts' },
        { code: '079F', description: 'Miscellaneous Expense - Parts' },
        { code: '080F', description: 'Rent & Lease Expense - Parts' },
        { code: '082F', description: 'Repairs & Maintenance - Parts' },
        { code: '084F', description: 'Real Estate Taxes - Parts' },
        { code: '087F', description: 'Heat, Light, Water - Parts' },
        { code: '088F', description: 'Insurance Other - Parts' },
        { code: '089F', description: 'Taxes Other - Parts' },
        { code: '090F', description: 'Other Interest - Parts' },
        { code: '091F', description: 'Depreciation Equipment - Parts' },
      ]
    },
    {
      name: 'OTHERS',
      displayName: 'Others',
      accounts: [
        { code: '020F', description: 'Compensation Owners - Parts' },
      ]
    }
  ]
};

/**
 * BODY SHOP Department
 */
const BODY_SHOP: DepartmentTemplate = {
  name: 'BODY_SHOP',
  displayName: 'Body Shop',
  hasRevenue: true,
  hasFandI: false,
  suffixConfig: {
    suffixes: ['CVC', 'EG', 'E', 'R'],  // Order: longest first for parsing, then alphabetical
    salesBaseNumbers: ['021', '023', '029', '061', '062', '067', '075', '076', '092', '450SS'],
    fixedBaseNumbers: ['022', '025', '026', '027', '028', '051', '060', '066', '068', '070', '071', '072', '073', '074', '078', '079', '080', '082', '084', '087', '088', '089', '091'],
    othersBaseNumbers: ['020'],
    creditBaseNumbers: [],
  },
  expenseCategories: [
    {
      name: 'SALES',
      displayName: 'Sales Expenses',
      accounts: [
        { code: '021CVC', description: 'Compensation Supervisors - CVC' },
        { code: '021E', description: 'Compensation Mgrs - Body Shop' },
        { code: '021EG', description: 'Compensation Mgrs Bodyshop' },
        { code: '023CVC', description: 'Other Salary & Wages - CVC' },
        { code: '023E', description: 'Other Salary & Wages - Body Shop' },
        { code: '023EG', description: 'Other Salary & Wages - Body Shop' },
        { code: '023R', description: 'Other Salary & Wages - Recon' },
        { code: '029E', description: 'Absentee Wages Body Shop' },
        { code: '029EG', description: 'Absentee Wages Body Shop' },
        { code: '061CVC', description: 'Tools & Supplies - CVC' },
        { code: '061E', description: 'Tools & Supplies - Body Shop' },
        { code: '061EG', description: 'Tools & Supplies - Body Shop' },
        { code: '061R', description: 'Tools & Supplies - Recon' },
        { code: '062CVC', description: 'Advertising - CVC' },
        { code: '062E', description: 'Advertising - Body Shop' },
        { code: '062EG', description: 'Advertising - Body Shop' },
        { code: '062R', description: 'Advertising - Body Shop Rust' },
        { code: '067CVC', description: 'Policy Expense - CVC' },
        { code: '067E', description: 'Policy Expense Body Shop' },
        { code: '067EG', description: 'Policy Expense - Body Shop' },
        { code: '067R', description: 'Policy Expense Body Shop Rust' },
        { code: '075CVC', description: 'Postage/Freight - CVC' },
        { code: '075E', description: 'Postage/Freight - Body Shop' },
        { code: '075EG', description: 'Postage/Freight - Body Shop' },
        { code: '076CVC', description: 'Schools & Seminars - CVC' },
        { code: '076E', description: 'Schools & Seminars - Body Shop' },
        { code: '076EG', description: 'Schools & Seminars - Body Shop' },
        { code: '092CVC', description: 'Equipment Repairs - CVC' },
        { code: '092E', description: 'Equipment Repairs - Body Shop' },
        { code: '092EG', description: 'Equipment Repairs - Body Shop' },
        { code: '450SSCVC', description: 'Repair Shop Material - SSCVC' },
      ]
    },
    {
      name: 'FIXED',
      displayName: 'Fixed Expenses',
      accounts: [
        { code: '022CVC', description: 'Compensation Clerical - CVC' },
        { code: '022E', description: 'Compensation Clerical - Body Shop' },
        { code: '022EG', description: 'Compensation Clerical - Body Shop' },
        { code: '025CVC', description: 'Payroll Taxes - CVC' },
        { code: '025E', description: 'Payroll Taxes - Body Shop' },
        { code: '025EG', description: 'Payroll Taxes - Body Shop' },
        { code: '025R', description: 'Payroll Taxes - Recon' },
        { code: '026CVC', description: 'Employee Benefits - CVC' },
        { code: '026E', description: 'Employee Benefits - Body Shop' },
        { code: '026EG', description: 'Employee Benefits - Body Shop' },
        { code: '027CVC', description: 'Pension - CVC' },
        { code: '027E', description: 'Pension - Body Shop' },
        { code: '027EG', description: 'Pension - Body Shop' },
        { code: '027R', description: 'Pension - Recon' },
        { code: '028E', description: 'Workers Comp Insurance - Body Shop' },
        { code: '028EG', description: 'Workers Comp Insurance - Body Shop' },
        { code: '051CVC', description: 'Company Vehicle - CVC' },
        { code: '051E', description: 'Company Vehicle - Body Shop' },
        { code: '051EG', description: 'Company Vehicle - Body Shop' },
        { code: '060CVC', description: 'Office Supplies - CVC' },
        { code: '060E', description: 'Office Supplies - Body Shop' },
        { code: '060EG', description: 'Office Supplies - Body Shop' },
        { code: '066E', description: 'Contributions - Body Shop' },
        { code: '068CVC', description: 'Outside Services - CVC' },
        { code: '068E', description: 'Outside Services - Body Shop' },
        { code: '068EG', description: 'Outside Services - Body Shop' },
        { code: '070CVC', description: 'Travel & Entertainment - CVC' },
        { code: '070E', description: 'Travel & Entertainment - Body Shop' },
        { code: '070EG', description: 'Travel & Entertainment - Body Shop' },
        { code: '071CVC', description: 'Membership, Dues, Publications - CVC' },
        { code: '071E', description: 'Membership, Dues, Publications - Body Shop' },
        { code: '071EG', description: 'Membership, Dues - Body Shop' },
        { code: '072E', description: 'Legal & Audit - RMD Body Shop' },
        { code: '073CVC', description: 'Meals & Entertainment - CVC' },
        { code: '073E', description: 'Meals & Entertainment - Body Shop' },
        { code: '073EG', description: 'Meals & Entertainment - Body Shop' },
        { code: '074CVC', description: 'Telephone - CVC' },
        { code: '074E', description: 'Telephone - Body Shop' },
        { code: '074EG', description: 'Telephone - Body Shop' },
        { code: '078CVC', description: 'Data Processing - CVC' },
        { code: '078E', description: 'Data Processing - Body Shop' },
        { code: '078EG', description: 'Data Processing - Body Shop' },
        { code: '079CVC', description: 'Miscellaneous Expense - CVC' },
        { code: '079E', description: 'Miscellaneous Expense - Body Shop' },
        { code: '079EG', description: 'Miscellaneous Expense - Body Shop' },
        { code: '080CVC', description: 'Rent & Lease Expense - CVC' },
        { code: '080E', description: 'Rent & Lease Expense - Body Shop' },
        { code: '080EG', description: 'Rent & Lease Expense - Body Shop' },
        { code: '082CVC', description: 'Repairs & Maintenance - CVC' },
        { code: '082E', description: 'Repairs & Maintenance - Body Shop' },
        { code: '082EG', description: 'Repairs & Maintenance - Body Shop' },
        { code: '084CVC', description: 'Real Estate Taxes - CVC' },
        { code: '084E', description: 'Real Estate Taxes - Body Shop' },
        { code: '084EG', description: 'Real Estate Taxes - Body Shop' },
        { code: '084R', description: 'Real Estate Taxes - Body Shop' },
        { code: '087CVC', description: 'Heat, Light, Water - CVC' },
        { code: '087E', description: 'Heat, Light, Water - Body Shop' },
        { code: '087EG', description: 'Heat, Light, Water - Body Shop' },
        { code: '088E', description: 'Insurance Other - Body Shop' },
        { code: '088EG', description: 'Insurance Other - Body Shop' },
        { code: '089CVC', description: 'Taxes Other - CVC' },
        { code: '089E', description: 'Taxes Other - Body Shop' },
        { code: '089EG', description: 'Taxes Other - Body Shop' },
        { code: '091CVC', description: 'Depreciation Equipment - CVC' },
        { code: '091E', description: 'Depreciation Equipment - Body Shop' },
        { code: '091EG', description: 'Depreciation Equipment - Body Shop' },
      ]
    },
    {
      name: 'OTHERS',
      displayName: 'Others',
      accounts: [
        { code: '020E', description: 'Compensation Owners - Body Shop' },
        { code: '020EG', description: 'Compensation Owners - Body Shop' },
      ]
    }
  ]
};

/**
 * RENTAL Department
 *
 * Structure:
 * - RENTAL INCOME (4 accounts) - Credit accounts for rental revenue
 * - RENTAL SELLING EXPENSE (7 accounts) - Variable/selling expenses
 * - RENTAL FIXED EXPENSE (20 accounts) - Fixed overhead expenses
 *
 * Display order:
 * 1. Rental Income (total)
 * 2. Rental Selling Expense (total)
 * 3. Rental Selling Gross = Income - Selling Expense
 * 4. Rental Fixed Expense (total)
 * 5. Total Expenses = Selling + Fixed
 * 6. Rental Operating Profit = Income - Total Expenses
 */
const RENTAL: DepartmentTemplate = {
  name: 'RENTAL',
  displayName: 'Rental',
  hasRevenue: false, // Rental uses custom income structure, not standard Sales/COS
  hasFandI: false,
  expenseCategories: [
    {
      name: 'RENTAL_INCOME',
      displayName: 'Rental Income',
      accounts: [
        { code: '805', description: 'Rental Vehicle Income', sign: 'credit' },
        { code: '805GG', description: 'Rental Department Income', sign: 'credit' },
        { code: '806', description: 'Rental CCDW Income', sign: 'credit' },
        { code: '806GG', description: 'Rental Department CCDW Income', sign: 'credit' },
      ]
    },
    {
      name: 'RENTAL_SELLING',
      displayName: 'Rental Selling Expense',
      accounts: [
        { code: '015GG', description: 'Policy Expense - Rental Department' },
        { code: '019GG', description: 'Advertising - Rental Department' },
        { code: '021GG', description: 'Compensation Supervisors - Rental' },
        { code: '022GG', description: 'Compensation Clerical - Rental Dept.' },
        { code: '023GG', description: 'Other Salary & Wages - Rental Dept.' },
        { code: '061GG', description: 'Tools & Supplies - Rental Dept.' },
        { code: '904', description: 'Wages - Rental Department' },
      ]
    },
    {
      name: 'RENTAL_FIXED',
      displayName: 'Rental Fixed Expense',
      accounts: [
        { code: '025GG', description: 'Payroll Taxes - Rental Dept.' },
        { code: '026GG', description: 'Employee Benefits - Rental Dept.' },
        { code: '027GG', description: 'Pension - Rental Dept.' },
        { code: '051GG', description: 'Company Vehicle - Rental Dept.' },
        { code: '060GG', description: 'Office Supplies - Rental Dept.' },
        { code: '066GG', description: 'Contributions - Rental Dept.' },
        { code: '068GG', description: 'Outside Services - Rental Dept.' },
        { code: '073GG', description: 'Meals & Entertainment - Rental Dept.' },
        { code: '074GG', description: 'Telephone - Rental Dept.' },
        { code: '078GG', description: 'Data Processing - Rental Dept.' },
        { code: '079GG', description: 'Miscellaneous Expense - Rental Dept.' },
        { code: '080GG', description: 'Rent & Lease Expense - Rental Dept.' },
        { code: '082GG', description: 'Repairs & Maintenance - Rental Dept.' },
        { code: '084GG', description: 'Real Estate Taxes - Rental Dept.' },
        { code: '087GG', description: 'Heat, Light, Water - Rental Dept.' },
        { code: '088GG', description: 'Insurance Other - Rental Dept.' },
        { code: '089GG', description: 'Taxes Other - Rental Dept.' },
        { code: '091GG', description: 'Depreciation Equipment - Rental Dept.' },
        { code: '903', description: 'Rental Vehicle Depreciation' },
        { code: '905', description: 'Rental Expense' },
      ]
    }
  ]
};

/**
 * OTHERS Department (Other Income)
 * Income-only department with manufacturer bonuses, DOC fees, etc.
 * No expenses - just displays total income from these accounts.
 */
const OTHERS: DepartmentTemplate = {
  name: 'OTHERS',
  displayName: 'Others',
  hasRevenue: false, // We'll handle this specially - income only, no sales/COS
  hasFandI: false,
  expenseCategories: [
    {
      name: 'OTHER_INCOME',
      displayName: 'Other Income',
      accounts: [
        { code: '809', description: 'Other Income', sign: 'credit' },
        { code: '815A', description: 'Volvo - Retailer Standard Bonus New/Used', sign: 'credit' },
        { code: '815AC', description: 'Volvo - 360 Bonus', sign: 'credit' },
        { code: '816A', description: 'Volvo - Training Certification New', sign: 'credit' },
        { code: '817A', description: 'Volvo - Customer Experience Bonus New', sign: 'credit' },
        { code: '818A', description: 'Volvo - US Options/Package Bonus New', sign: 'credit' },
        { code: '818AC', description: 'Volvo - BEV 2% Min Ad Price Bonus New', sign: 'credit' },
        { code: '819ACC', description: 'Audi - Margin Bonus - Audi Connect Customer', sign: 'credit' },
        { code: '819B', description: 'Audi - Margin Bonus - Sales Bonus', sign: 'credit' },
        { code: '819CL', description: 'Audi - Service Loyalty Bonus - Sales Bonus', sign: 'credit' },
        { code: '819M', description: 'Audi - Margin Bonus - Market Bonus', sign: 'credit' },
        { code: '823', description: 'Retail Warranty Markup', sign: 'credit' },
        { code: '850', description: 'DOC Fee', sign: 'credit' },
      ]
    }
  ]
};

/**
 * MISC Department (Clearing Accounts)
 */
const MISC: DepartmentTemplate = {
  name: 'MISC',
  displayName: 'Misc Clearing Accounts',
  hasRevenue: false,
  hasFandI: false,
  expenseCategories: [
    {
      name: 'CLEARING',
      displayName: 'Clearing Accounts',
      accounts: [
        { code: '011X', description: 'Salespeople Compen Expense Allocation' },
        { code: '019', description: 'Advertising Clearing' },
        { code: '020', description: 'Compensation Owners Clearing' },
        { code: '022', description: 'Compensation Clerical Clearing' },
        { code: '022X', description: 'Compensation Clerical' },
        { code: '023', description: 'Other Salary & Wages Clearing' },
        { code: '023P', description: 'Other Salary & Wages - Wholesale Parts' },
        { code: '024', description: 'Salary General Manager Clearing' },
        { code: '025', description: 'Payroll Taxes Clearing' },
        { code: '025P', description: 'Payroll Taxes W/S Expense Allocation' },
        { code: '025X', description: 'Payroll Taxes Clearing' },
        { code: '026', description: 'Employee Benefits Clearing' },
        { code: '027', description: 'Pension Clearing' },
        { code: '027P', description: 'Pension - W/S PA Exp Allocation' },
        { code: '027X', description: 'Pension Clearing' },
        { code: '028', description: 'Workers Comp Insurance Clearing' },
        { code: '051', description: 'Company Vehicle Clearing' },
        { code: '051P', description: 'Fuel W/S Parts A Expense Allocation' },
        { code: '060', description: 'Office Supplies Clearing' },
        { code: '061', description: 'Tools & Supplies Clearing' },
        { code: '061P', description: 'Tools & Supplies - Wholesale Parts Allocation' },
        { code: '062', description: 'Advertising Clearing' },
        { code: '066', description: 'Contributions Clearing' },
        { code: '068', description: 'Outside Services Clearing' },
        { code: '070', description: 'Travel & Entertainment Clearing' },
        { code: '071', description: 'Membership, Dues, Publications Clearing' },
        { code: '072', description: 'Legal & Audit Clearing' },
        { code: '073', description: 'Meals & Entertainment Clearing' },
        { code: '074', description: 'Telephone Clearing' },
        { code: '075', description: 'Postage/Freight Clearing' },
        { code: '076', description: 'Schools & Seminars Clearing' },
        { code: '078', description: 'Data Processing Clearing' },
        { code: '078P', description: 'Data Processing W/S Allocation' },
        { code: '079', description: 'Miscellaneous Expense Clearing' },
        { code: '080', description: 'Rent & Lease Expense Clearing' },
        { code: '082', description: 'Repairs & Maintenance Clearing' },
        { code: '084', description: 'Real Estate Taxes Clearing' },
        { code: '085', description: 'Insurance Buildings Clearing' },
        { code: '087', description: 'Heat, Light, Water Clearing' },
        { code: '088', description: 'Insurance Other Clearing' },
        { code: '089', description: 'Taxes Other Clearing' },
        { code: '091', description: 'Depreciation Equipment Clearing' },
        { code: '091P', description: 'Depreciation Equipment - Whsle Parts Allocation' },
        { code: '092', description: 'Equipment Repairs Clearing' },
      ]
    }
  ]
};

/**
 * Complete DOC Template - all departments in display order
 */
export const DOC_TEMPLATE: DepartmentTemplate[] = [
  NEW_VEHICLE,
  USED_VEHICLE,
  SERVICE,
  PARTS,
  BODY_SHOP,
  OTHERS,
  RENTAL,
  MISC,
];

/**
 * Build a lookup map from account code to department/category
 */
export function buildAccountLookup(): Map<string, { department: string; category: string; description: string; sign: 'credit' | 'debit' }> {
  const lookup = new Map<string, { department: string; category: string; description: string; sign: 'credit' | 'debit' }>();

  for (const dept of DOC_TEMPLATE) {
    for (const cat of dept.expenseCategories) {
      for (const acc of cat.accounts) {
        lookup.set(acc.code, {
          department: dept.name,
          category: cat.name,
          description: acc.description,
          sign: acc.sign || 'debit'
        });
      }
    }
  }

  return lookup;
}

/**
 * Get all account codes from the template
 */
export function getAllAccountCodes(): string[] {
  const codes: string[] = [];
  for (const dept of DOC_TEMPLATE) {
    for (const cat of dept.expenseCategories) {
      for (const acc of cat.accounts) {
        codes.push(acc.code);
      }
    }
  }
  return codes;
}

// ========================================
// SUFFIX-BASED PATTERN MATCHING
// ========================================

/**
 * Parse an account code into base number and suffix, given a set of valid suffixes.
 * Suffixes are tried longest-first to handle overlaps (e.g., 'EG' before 'E').
 *
 * Example: parseAccountCode('029CVC', ['CVC', 'EG', 'E', 'R']) → { base: '029', suffix: 'CVC' }
 * Example: parseAccountCode('450SSCVC', ['CVC', 'EG', 'E', 'R']) → { base: '450SS', suffix: 'CVC' }
 */
export function parseAccountCode(
  code: string,
  suffixes: string[]
): { base: string; suffix: string } | null {
  // Sort suffixes by length descending to match longest first (EG before E)
  const sorted = [...suffixes].sort((a, b) => b.length - a.length);
  for (const suffix of sorted) {
    if (code.endsWith(suffix)) {
      const base = code.slice(0, code.length - suffix.length);
      if (base.length > 0) {
        return { base, suffix };
      }
    }
  }
  return null;
}

/**
 * Generate a description for a discovered account by finding a sibling with the same base number.
 * Falls back to a generic description if no sibling found.
 *
 * Example: base '029', suffix 'CVC', sibling '029E' = "Absentee Wages Body Shop"
 *   → "Absentee Wages - CVC"
 */
function generateDescription(
  base: string,
  suffix: string,
  categories: ExpenseCategory[],
  suffixes: string[]
): string {
  // Find any sibling account (same base, different suffix) for description template
  for (const cat of categories) {
    for (const acc of cat.accounts) {
      const parsed = parseAccountCode(acc.code, suffixes);
      if (parsed && parsed.base === base) {
        // Extract the common part of the description (before the suffix-specific part)
        const desc = acc.description;
        // Common patterns: "Something - Body Shop", "Something Body Shop", "Something - CVC", etc.
        // Try to replace the suffix-specific department name
        const suffixNames: Record<string, string[]> = {
          'CVC': ['CVC', 'SSCVC'],
          'E': ['Body Shop', 'BS'],
          'EG': ['Body Shop', 'Bodyshop', 'Body Shop'],
          'R': ['Recon', 'Body Shop Rust', 'Rust'],
        };
        // Get the target suffix display name
        const targetName = suffix === 'R' ? 'Recon' : suffix;
        // Try to find and replace the source suffix name in the description
        const sourceNames = suffixNames[parsed.suffix] || [];
        for (const srcName of sourceNames) {
          if (desc.includes(srcName)) {
            return desc.replace(srcName, targetName);
          }
        }
        // If no pattern matched, append suffix to base description
        const dashIdx = desc.lastIndexOf(' - ');
        if (dashIdx > 0) {
          return desc.substring(0, dashIdx) + ' - ' + targetName;
        }
        return desc + ' - ' + targetName;
      }
    }
  }
  // No sibling found at all — generate generic description
  return `Account ${base} - ${suffix}`;
}

/**
 * Category name mapping for suffix config
 */
const SUFFIX_CATEGORY_MAP: Record<string, string> = {
  salesBaseNumbers: 'SALES',
  fixedBaseNumbers: 'FIXED',
  othersBaseNumbers: 'OTHERS',
};

/**
 * Resolve expense categories for a department by merging explicit template accounts
 * with pattern-discovered accounts from available data.
 *
 * For departments without suffixConfig, returns the original categories unchanged.
 * For departments with suffixConfig (currently only BODY_SHOP):
 *   1. Starts with all explicit template accounts (preserving descriptions and sign overrides)
 *   2. Scans availableAccountCodes for codes matching known suffixes
 *   3. If a matching code isn't already in the template, adds it to the appropriate category
 *   4. Unknown base numbers are skipped (not added to any category)
 *
 * @param dept - The department template
 * @param availableAccountCodes - Account codes available in the data (from DynamoDB or API response)
 * @returns Resolved expense categories with discovered accounts appended
 */
export function resolveAccountsForDepartment(
  dept: DepartmentTemplate,
  availableAccountCodes: string[]
): ExpenseCategory[] {
  if (!dept.suffixConfig) {
    return dept.expenseCategories;
  }

  const { suffixes, salesBaseNumbers, fixedBaseNumbers, othersBaseNumbers, creditBaseNumbers } = dept.suffixConfig;

  // Build set of all explicitly listed account codes for quick lookup
  const explicitCodes = new Set<string>();
  for (const cat of dept.expenseCategories) {
    for (const acc of cat.accounts) {
      explicitCodes.add(acc.code);
    }
  }

  // Build base number → category name lookup
  const baseToCategoryName = new Map<string, string>();
  for (const base of salesBaseNumbers) baseToCategoryName.set(base, 'SALES');
  for (const base of fixedBaseNumbers) baseToCategoryName.set(base, 'FIXED');
  for (const base of othersBaseNumbers) baseToCategoryName.set(base, 'OTHERS');
  const creditBases = new Set(creditBaseNumbers);

  // Collect discovered accounts by category name
  const discoveredByCategory = new Map<string, AccountDefinition[]>();

  for (const code of availableAccountCodes) {
    // Skip if already explicitly listed
    if (explicitCodes.has(code)) continue;

    const parsed = parseAccountCode(code, suffixes);
    if (!parsed) continue;

    // Determine which category this belongs to
    const categoryName = baseToCategoryName.get(parsed.base);
    if (!categoryName) {
      // Unknown base number — not a Body Shop account, skip
      continue;
    }

    const sign: 'credit' | 'debit' | undefined = creditBases.has(parsed.base) ? 'credit' : undefined;
    const description = generateDescription(parsed.base, parsed.suffix, dept.expenseCategories, suffixes);

    const accDef: AccountDefinition = { code, description };
    if (sign) accDef.sign = sign;

    if (!discoveredByCategory.has(categoryName)) {
      discoveredByCategory.set(categoryName, []);
    }
    discoveredByCategory.get(categoryName)!.push(accDef);
  }

  // If nothing was discovered, return original categories
  if (discoveredByCategory.size === 0) {
    return dept.expenseCategories;
  }

  // Deep-copy categories and append discovered accounts
  return dept.expenseCategories.map(cat => {
    const discovered = discoveredByCategory.get(cat.name);
    if (!discovered || discovered.length === 0) {
      return cat;
    }
    // Sort discovered accounts by code for deterministic order
    discovered.sort((a, b) => a.code.localeCompare(b.code));
    return {
      ...cat,
      accounts: [...cat.accounts, ...discovered],
    };
  });
}
